/**
 * − value + (typing still works) with its unit, and optional one-click
 * presets beside it as choice buttons — minutes on the Schedule, visits and
 * drops on the Hour Optimizer. The field is the soft grey one.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Minus, Plus } from 'lucide-react';

import { NumberField } from '@/components/ui';
import { cn } from '@/lib/utils';

import { CHOICE_CHIP, CHOICE_OFF, CHOICE_ON, MINIMAL_FIELD } from './field-style';

interface Props {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  ariaLabel: string;
  /** Shown after the value (default "min"). */
  unit?: string;
  presets?: readonly number[];
}

const STEP_BUTTON =
  'flex h-8 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30';

export const NumberStepper: FC<Props> = ({ value, onChange, min, max, step, ariaLabel, unit = 'min', presets }) => (
  <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
    <div className="inline-flex items-center gap-0.5">
      <button type="button" aria-label={`${ariaLabel}: ${step} less`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - step))} className={STEP_BUTTON}>
        <Minus className="h-3.5 w-3.5" />
      </button>
      <NumberField value={value} onChange={onChange} min={min} max={max} aria-label={ariaLabel} className={cn(MINIMAL_FIELD, 'w-16 text-center')} />
      <button type="button" aria-label={`${ariaLabel}: ${step} more`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + step))} className={STEP_BUTTON}>
        <Plus className="h-3.5 w-3.5" />
      </button>
      <span className="ml-1 text-[13px] text-muted-foreground">{unit}</span>
    </div>
    {presets ? (
      <div className="inline-flex flex-wrap items-center gap-1.5">
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={p === value}
            aria-label={`${p.toLocaleString('en-US')} ${unit}`}
            onClick={() => onChange(p)}
            className={cn(CHOICE_CHIP, 'min-w-10 justify-center px-2.5 font-mono tabular-nums', p === value ? CHOICE_ON : CHOICE_OFF)}
          >
            {p.toLocaleString('en-US')}
          </button>
        ))}
      </div>
    ) : null}
  </div>
);
